import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { TopBar } from '../components/layout/TopBar';
import { Card } from '../components/ui/Card';
import { StatCard } from '../components/ui/StatCard';
import { cn } from '../lib/cn';
import { fmt } from '../lib/format';

interface Summary {
  today: { dynamic: string; leadership: string; sameRank: string; total: string };
  allTime: { dynamic: string; leadership: string; sameRank: string; total: string };
}

interface Tx {
  id: string;
  type: string;
  asset: string;
  amount: string;
  genLevel: number | null;
  counterparty: string | null;
  createdAt: string;
}

type Filter = 'ALL' | 'DYNAMIC' | 'LEADERSHIP' | 'SAME_RANK';
const FILTERS: Filter[] = ['ALL', 'DYNAMIC', 'LEADERSHIP', 'SAME_RANK'];

export function BonusPage() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [filter, setFilter] = useState<Filter>('ALL');
  const [txs, setTxs] = useState<Tx[]>([]);

  useEffect(() => { api<Summary>('/wallet/bonus/summary').then(setSummary).catch(() => { /* */ }); }, []);

  useEffect(() => {
    api<{ transactions: Tx[] }>(`/wallet/bonus/details?type=${filter}&limit=50`)
      .then((r) => setTxs(r.transactions))
      .catch(() => { /* */ });
  }, [filter]);

  return (
    <>
      <TopBar title="Bonuses" />
      <div className="px-4 pt-4 space-y-4">
        {/* Today */}
        <Card>
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-bold">Today</div>
            <div className="text-lg font-extrabold font-mono text-accent">
              +{summary ? fmt(summary.today.total) : '—'} <span className="text-xs text-fg-muted font-sans">USDT</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <StatCard label="Dynamic" value={fmt(summary?.today.dynamic ?? 0)} />
            <StatCard label="Leader" value={fmt(summary?.today.leadership ?? 0)} />
            <StatCard label="Peer" value={fmt(summary?.today.sameRank ?? 0)} />
          </div>
        </Card>

        {/* All time */}
        <Card>
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-bold">All Time</div>
            <div className="text-lg font-extrabold font-mono">
              +{summary ? fmt(summary.allTime.total) : '—'} <span className="text-xs text-fg-muted font-sans">USDT</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <StatCard label="Dynamic" value={fmt(summary?.allTime.dynamic ?? 0)} />
            <StatCard label="Leader" value={fmt(summary?.allTime.leadership ?? 0)} />
            <StatCard label="Peer" value={fmt(summary?.allTime.sameRank ?? 0)} />
          </div>
        </Card>

        {/* Filter chips */}
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={cn(
                'shrink-0 px-3.5 h-8 rounded-full text-xs font-semibold border transition-colors',
                filter === f
                  ? 'bg-accent text-white border-accent'
                  : 'bg-surface-2 text-fg-muted border-border hover:border-accent',
              )}
            >
              {f.replace('_', ' ')}
            </button>
          ))}
        </div>

        {/* Details list */}
        <Card padded={false}>
          {txs.length === 0 ? (
            <div className="p-6 text-center text-sm text-fg-muted">No bonus entries</div>
          ) : (
            <ul className="divide-y divide-border">
              {txs.map((t) => (
                <li key={t.id} className="p-3.5 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-accent/15 text-accent flex items-center justify-center text-[10px] font-bold">
                    {t.genLevel ? `G${t.genLevel}` : prettyKind(t.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">{prettyType(t.type)}</div>
                    <div className="text-[11px] text-fg-muted">{new Date(t.createdAt).toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold font-mono text-success">+{fmt(t.amount)}</div>
                    <div className="text-[10px] uppercase tracking-wider text-fg-muted">{t.asset}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}

function prettyType(t: string) {
  return t.replace(/^BONUS_/, '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
function prettyKind(t: string) {
  if (t === 'BONUS_LEADERSHIP') return 'LD';
  if (t === 'BONUS_SAME_RANK') return 'SR';
  return 'B';
}
